/**
 * The PDF extraction screen's three calls - `getGrabbedImage`, `getPreview` and
 * `saveExtraction` - as REST requests.
 *
 * What the screen holds. The viewer (public/pdf/js/ipdf.js) reports a drawn
 * selection in screen pixels, with the pixel box of every page the selection
 * touches. PdfDoc sends that as `{pdfId, source, destination}` to
 * `getGrabbedImage`; PdfExtraction keeps the same object as the record of the
 * extraction and hands the list back to `getPreview`; the save modal hands it to
 * `saveExtraction`. So all three calls carry one shape, and the API takes
 * another: a list of `{page, rotation, rectangle: {x, y, width, height}}`.
 *
 * Main's server turned the pixels into a region of the page in PDF points with
 * the page sizes it read out of the file (`PdfRectangle::createSubPdfRectangle`,
 * `PdfUtility::extractPdf`). The API takes the region itself - in points,
 * measured from the bottom left corner of the page as the file draws it, which
 * is how a PDF measures and how `document_extraction_pages.crop_*` stores it -
 * so that conversion happens here, from the `pdfSize` the viewer reports with
 * each page. It is the one thing the browser knows and the API does not: the
 * file's own page size is not in the API's page inventory, and a region guessed
 * from pixels alone would cut some other part of a clinical document.
 *
 * Nothing in here guesses. A selection that cannot be placed on its page - no
 * size, no such page, a rotation no viewer sends - is refused before a request
 * leaves, in the call site's own failure envelope, because an extraction that
 * quietly cut the wrong region would be filed on the chart as the report.
 */

import api, { ApiError } from './client';
import { fileRef } from './clinicalBridge';
import { okMessage } from './bridgeShapes';

const QUARTER_TURNS = [0, 90, 180, 270];

/**
 * A request the bridge will not send: the document or the selection could not
 * be placed. An `ApiError`, so the bridge re-emits it in the failure envelope
 * the call site reads instead of letting the promise reject unhandled.
 */
export class ExtractionRequestError extends ApiError {
  constructor(message, code = 'bridge_extraction_unplaceable') {
    super({ status: 0, code, message });
    this.name = 'ExtractionRequestError';
  }
}

const UNPLACEABLE = 'This selection could not be placed on its page. Reload the page and try again.';

/** The document's route key, or a refusal: a path built from nothing is the wrong document's. */
function documentRef(value) {
  const ref = fileRef(value);

  if (ref === null) {
    throw new ExtractionRequestError(
      'This action could not be sent: the document it applies to was not identified. Reload the page and try again.',
      'bridge_unresolved_target',
    );
  }

  return ref;
}

/** `crop_*` are stored to four decimals; anything finer is floating-point noise. (`+ 0`: a rounded -0 is 0.) */
const toFourDecimals = (value) => Math.round(value * 10000) / 10000 + 0;

/** A pixel box by its four measured edges. The viewer's right/bottom are not read: they are these two sums. */
function pixelBox(rectangle) {
  const { top, left, width, height } = rectangle || {};

  if (![top, left, width, height].every(Number.isFinite) || width <= 0 || height <= 0) {
    throw new ExtractionRequestError(UNPLACEABLE);
  }

  return { top, left, width, height, right: left + width, bottom: top + height };
}

function quarterTurns(rotation) {
  const turned = ((Number(rotation ?? 0) % 360) + 360) % 360;

  if (!QUARTER_TURNS.includes(turned)) throw new ExtractionRequestError(UNPLACEABLE);

  return turned;
}

function pageNumber(value) {
  const number = Number(value);

  if (!Number.isInteger(number) || number < 1) throw new ExtractionRequestError(UNPLACEABLE);

  return number;
}

function pageSize(referenced) {
  const { width, height } = (referenced && referenced.pdfSize) || {};

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new ExtractionRequestError(UNPLACEABLE);
  }

  return { width, height };
}

/**
 * Where a selection falls on its page, in PDF points.
 *
 * Main's `PdfRectangle::createSubPdfRectangle`, with the page's own origin at
 * 0,0. `page` is the page's pixel box as displayed, `size` its width and height
 * in points before the viewer rotated it, `selected` the part of the selection
 * on this page in pixels, `rotation` the viewer's rotation.
 *
 * Measured from the file's own bottom left corner, which is a different screen
 * corner at each quarter turn - the page is displayed turned, the file is not -
 * and a quarter turn swaps which screen axis carries the page's width.
 */
function regionInPoints(page, size, selected, rotation) {
  const turned = rotation === 90 || rotation === 270;
  const perPixelAcross = size.width / (turned ? page.height : page.width);
  const perPixelUp = size.height / (turned ? page.width : page.height);

  switch (rotation) {
    case 90:
      return {
        x: (selected.top - page.top) * perPixelAcross,
        y: (selected.left - page.left) * perPixelUp,
        width: selected.height * perPixelAcross,
        height: selected.width * perPixelUp,
      };
    case 180:
      return {
        x: (page.right - selected.right) * perPixelAcross,
        y: (selected.top - page.top) * perPixelUp,
        width: selected.width * perPixelAcross,
        height: selected.height * perPixelUp,
      };
    case 270:
      return {
        x: (page.bottom - selected.bottom) * perPixelAcross,
        y: (page.right - selected.right) * perPixelUp,
        width: selected.height * perPixelAcross,
        height: selected.width * perPixelUp,
      };
    default:
      return {
        x: (selected.left - page.left) * perPixelAcross,
        y: (page.bottom - selected.bottom) * perPixelUp,
        width: selected.width * perPixelAcross,
        height: selected.height * perPixelUp,
      };
  }
}

/**
 * One extraction as the API's pages: one `{page, rotation, rectangle}` for each
 * page the selection touches, top to bottom.
 *
 * The LAST selection, as main's server cut it (`PdfUtility::getGrabbedImage` and
 * `generateFullExtraction` both take `selections[count - 1]`). A selection that
 * crosses a page break is one picture in main, the slices joined; the API cuts
 * one page at a time, so it is one page per slice here and a merged PDF holds
 * them as separate pages.
 *
 * @param {{source: object}} extraction `{pdfId, source, destination}`, as PdfDoc builds it
 * @returns {Array<{page: number, rotation: number, rectangle: {x: number, y: number, width: number, height: number}}>}
 */
export function extractionPages(extraction) {
  const source = extraction && extraction.source;

  if (!source || typeof source !== 'object') throw new ExtractionRequestError(UNPLACEABLE);

  const rotation = quarterTurns(source.rotation);
  const selections = Array.isArray(source.selections) ? source.selections : [];
  const selection = selections[selections.length - 1];
  const intersections = selection && Array.isArray(selection.intersections) ? selection.intersections : [];
  const referencedPages = Array.isArray(source.referencedPages) ? source.referencedPages : [];

  if (intersections.length === 0) throw new ExtractionRequestError(UNPLACEABLE);

  return intersections.map((intersection) => {
    const number = pageNumber(intersection && intersection.pageNumber);
    const referenced = referencedPages.find((candidate) => Number(candidate && candidate.pageNumber) === number);

    if (!referenced) throw new ExtractionRequestError(UNPLACEABLE);

    const region = regionInPoints(
      pixelBox(referenced.rectangle),
      pageSize(referenced),
      pixelBox(intersection.rectangle),
      rotation,
    );

    return {
      page: number,
      rotation,
      rectangle: {
        x: toFourDecimals(region.x),
        y: toFourDecimals(region.y),
        width: toFourDecimals(region.width),
        height: toFourDecimals(region.height),
      },
    };
  });
}

/**
 * A list of extractions as the API's `extractions` / `pages`: every extraction's
 * pages, in the order the list holds them. An empty or absent list is empty:
 * for a report, "no carving" is the whole scan ("Save copy").
 */
export function reportExtractions(extractions) {
  return [].concat(...(Array.isArray(extractions) ? extractions : []).map((extraction) => extractionPages(extraction)));
}

/**
 * `getGrabbedImage` - the thumbnail of a drawn selection.
 *
 * Answers main's `{message: 'pdfGrabbedMessage', url}` with the picture as a
 * data URI; the API calls it `image`. A selection that crosses a page break is
 * several grabs, joined top to bottom in the browser as main's server joined
 * them (`ImageUtility::concatenateTiff`).
 *
 * @param {object} p `{pdfId, source, destination}`
 * @param {{stack?: function(string[]): Promise<string>}} deps the joining of slices, replaceable for a test
 */
export async function grabImage(p, deps = {}) {
  const pdf = documentRef(p && p.pdfId);
  const pages = extractionPages(p);

  const images = [];

  // One at a time and in order: the slices are joined in the order they arrive.
  for (const page of pages) {
    const grab = await api.post(`/pdf-documents/${pdf}/grabs`, page);

    images.push(grab && grab.image);
  }

  // Main's failure word (PdfUtility::getGrabbedImage); the call site rejects on anything but the success one.
  if (images.some((image) => typeof image !== 'string' || image === '')) return okMessage('pdfGrabFailedMessage');

  if (images.length === 1) return okMessage('pdfGrabbedMessage', { url: images[0] });

  try {
    return okMessage('pdfGrabbedMessage', { url: await (deps.stack || stackImages)(images) });
  } catch (stackingError) {
    return okMessage('pdfGrabFailedMessage');
  }
}

/**
 * `getPreview` - the extractions merged into one PDF.
 *
 * Main answered the PDF as base64 inside JSON and `pdfApi.getPreview` reads it
 * that way; the API streams the bytes. They are read from the blob and handed
 * on as the string that reader decodes, so the screen is untouched.
 *
 * The PDF is named `id` here (main's `DocumentRequest` keys `getPreview` on `id`
 * and `getGrabbedImage` on `pdfId`).
 *
 * @param {{id: string|number, extractions: object[]}} p
 */
export async function previewDocument(p) {
  const pdf = documentRef(p && (p.id ?? p.pdfId));
  const pages = reportExtractions(p.extractions);

  const blob = await api.post(`/pdf-documents/${pdf}/extraction/preview`, { pages }, { responseType: 'blob' });

  return okMessage('getPreviewSuccess', { preview: await base64Of(blob) });
}

/** The bytes of a blob as base64 - what `pdfApi.getPreview` decodes. */
function base64Of(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error || new Error('The preview could not be read.'));
    reader.readAsDataURL(blob);
  });
}

/** The slices of one selection as a single picture, one above the next. */
export async function stackImages(sources) {
  const images = await Promise.all(sources.map(loadImage));
  const canvas = document.createElement('canvas');

  canvas.width = Math.max(...images.map((image) => image.width));
  canvas.height = images.reduce((sum, image) => sum + image.height, 0);

  const context = canvas.getContext('2d');
  let top = 0;

  images.forEach((image) => {
    context.drawImage(image, 0, top);
    top += image.height;
  });

  return canvas.toDataURL('image/png');
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('A part of the selection could not be drawn.'));
    image.src = source;
  });
}
